import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Pencil, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../../config/settings-api';
import {
  buildTimeSlotGrid,
  buildTimeSlotSavePayload,
  isSlotConfigured,
  serializeTimeSlotGrid,
} from '../../../utils/timeSlotHelpers';
import './TimeSlotsSettings.css';

const TimeSlotsSettings = ({ defaultTimeSlots = [], timeSlotsList = [], canManage, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', startTime: '' });

  const columnCount = useMemo(() => rows[0]?.slots?.length || 7, [rows]);

  useEffect(() => {
    const grid = buildTimeSlotGrid(defaultTimeSlots, timeSlotsList);
    setRows(grid.rows);
    setInitialSnapshot(serializeTimeSlotGrid(grid.rows));
  }, [defaultTimeSlots, timeSlotsList]);

  const isChanged = rows.length > 0 && serializeTimeSlotGrid(rows) !== initialSnapshot;

  const openEditModal = (rowId, slotIndex, slot) => {
    setEditTarget({ rowId, slotIndex });
    setEditForm({
      name: slot?.name && slot.name !== '---' ? slot.name : '',
      startTime: slot?.startTime && slot.startTime !== '---' ? slot.startTime : '',
    });
  };

  const closeEditModal = () => {
    setEditTarget(null);
    setEditForm({ name: '', startTime: '' });
  };

  const handleCellClick = (rowId, slotIndex, slot) => {
    if (!canManage) return;
    if (isSlotConfigured(slot)) {
      setRows((prev) =>
        prev.map((row) =>
          row.rowId === rowId
            ? {
                ...row,
                slots: row.slots.map((item, index) =>
                  index === slotIndex ? { ...item, enabled: !item.enabled } : item
                ),
              }
            : row
        )
      );
    } else {
      openEditModal(rowId, slotIndex, slot);
    }
  };

  const handleEditSave = () => {
    if (!editTarget) return;

    const name = editForm.name.trim();
    const startTime = editForm.startTime.trim();

    if (!name || !startTime) {
      toast.error('Enter both slot name and start time');
      return;
    }

    setRows((prev) =>
      prev.map((row) =>
        row.rowId === editTarget.rowId
          ? {
              ...row,
              slots: row.slots.map((slot, index) =>
                index === editTarget.slotIndex
                  ? { ...slot, name, startTime, enabled: slot.enabled ?? true }
                  : slot
              ),
            }
          : row
      )
    );
    closeEditModal();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = buildTimeSlotSavePayload(rows);
      const response = await settingsAPI.updateTimeSlots(payload);

      if (response.success) {
        toast.success(response.msg || 'Time slots updated successfully');
        setInitialSnapshot(serializeTimeSlotGrid(rows));
        if (onSaved) onSaved();
      } else {
        toast.error(response.msg || response.error || 'Failed to update time slots');
      }
    } catch (error) {
      console.error('Failed to save time slots:', error);
      toast.error('Failed to update time slots');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-card settings-card--wide">
      <div className="settings-card-header">
        <div className="settings-card-header-left">
          <Clock size={20} />
          <h2>Time Slots</h2>
        </div>
      </div>

      <div className="settings-card-content">
        <p className="time-slots-help">
          Click a configured slot to enable or disable it. Use the edit icon to change name and start time.
          Empty slots can be clicked to add a new slot.
        </p>

        <div className="time-slots-table-wrapper">
          <table className="time-slots-table">
            <thead>
              <tr>
                <th>Day</th>
                {Array.from({ length: columnCount }).map((_, index) => (
                  <th key={`slot-head-${index}`}>Slot {index + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.rowId} className={row.isToday ? 'time-slots-row-today' : ''}>
                  <th className="time-slots-day-cell">
                    <span className="time-slots-day-label">{row.label}</span>
                    {row.subLabel && (
                      <span className={`time-slots-day-sub ${row.isToday ? 'today' : ''}`}>
                        {row.subLabel}
                      </span>
                    )}
                  </th>
                  {row.slots.map((slot, slotIndex) => {
                    const configured = isSlotConfigured(slot);
                    return (
                      <td key={`${row.rowId}-${slotIndex}`}>
                        <div
                          className={`time-slot-cell ${configured ? 'configured' : 'empty'} ${
                            configured && slot.enabled ? 'enabled' : ''
                          } ${canManage ? 'interactive' : ''}`}
                          onClick={() => handleCellClick(row.rowId, slotIndex, slot)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleCellClick(row.rowId, slotIndex, slot);
                            }
                          }}
                          role="button"
                          tabIndex={canManage ? 0 : -1}
                        >
                          <div className="time-slot-cell-content">
                            <span className="time-slot-name">
                              {configured ? slot.name : '—'}
                            </span>
                            <span className="time-slot-time">
                              {configured ? slot.startTime : 'Add slot'}
                            </span>
                          </div>
                          {canManage && (
                            <button
                              type="button"
                              className="time-slot-edit-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditModal(row.rowId, slotIndex, slot);
                              }}
                              title="Edit slot"
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {canManage && (
          <button
            type="button"
            className="settings-save-btn"
            onClick={handleSave}
            disabled={saving || !isChanged}
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Time Slots'}
          </button>
        )}
      </div>

      {editTarget && (
        <div className="time-slots-modal-overlay" onClick={closeEditModal}>
          <div className="time-slots-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Edit Time Slot</h3>
            <div className="settings-form-group">
              <label>Slot Name</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Morning"
              />
            </div>
            <div className="settings-form-group">
              <label>Start Time</label>
              <input
                type="time"
                value={editForm.startTime}
                onChange={(event) => setEditForm((prev) => ({ ...prev, startTime: event.target.value }))}
              />
            </div>
            <div className="time-slots-modal-actions">
              <button type="button" className="time-slots-modal-cancel" onClick={closeEditModal}>
                Cancel
              </button>
              <button type="button" className="settings-save-btn" onClick={handleEditSave}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeSlotsSettings;
